import { Box, Paper, Stack, Typography } from '@mui/material'

export function StatCard({
  label,
  value,
  accent,
}: {
  label: string
  value: string | number
  accent?: 'gold' | 'green' | 'amber' | 'rose' | 'slate' | 'blue'
}) {
  const colors = {
    gold: { bg: 'rgba(201,162,39,0.12)', text: '#9A7B1A' },
    green: { bg: 'rgba(45,157,120,0.12)', text: '#1d7a5c' },
    amber: { bg: 'rgba(232,168,56,0.14)', text: '#b87a12' },
    rose: { bg: 'rgba(214,69,69,0.1)', text: '#b83232' },
    slate: { bg: 'rgba(20,24,32,0.06)', text: '#141820' },
    blue: { bg: 'rgba(59,130,246,0.12)', text: '#2563eb' },
  }[accent || 'slate']

  return (
    <Paper className="admin-stat-card" elevation={0} sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: colors.text, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        {value}
      </Typography>
    </Paper>
  )
}

export function TerminalLogs({ title, logs, logEndRef }: { title: string; logs: { id: number; level: string; message: string; created_at: string }[]; logEndRef?: React.RefObject<HTMLDivElement | null> }) {
  return (
    <Box className="admin-terminal" sx={{ maxHeight: { xs: 280, sm: 380 }, overflow: 'auto', p: 0 }}>
      <Box className="admin-terminal-header">
        <Box className="admin-terminal-dot" sx={{ bgcolor: '#ff5f57' }} />
        <Box className="admin-terminal-dot" sx={{ bgcolor: '#febc2e' }} />
        <Box className="admin-terminal-dot" sx={{ bgcolor: '#28c840' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', ml: 1, fontFamily: 'monospace' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2, fontSize: 12, lineHeight: 1.7 }}>
        {logs.length === 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>Waiting for activity…</Typography>
        )}
        {logs.map((line) => (
          <Box key={line.id} sx={{ mb: 0.5, fontFamily: 'monospace' }}>
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)', mr: 1 }}>
              {line.created_at?.slice(11, 19)}
            </Box>
            <Box
              component="span"
              sx={{
                color:
                  line.level === 'error'
                    ? '#ff6b6b'
                    : line.level === 'warning'
                      ? '#ffd166'
                      : line.level === 'success'
                        ? '#69db7c'
                        : 'rgba(255,255,255,0.88)',
              }}
            >
              {line.message}
            </Box>
          </Box>
        ))}
        {logEndRef && <div ref={logEndRef} />}
      </Box>
    </Box>
  )
}

export function PageShell({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string
  subtitle?: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Box
      className="admin-dashboard-content"
      sx={{
        px: { xs: 1.5, sm: 2, md: 2.5 },
        py: { xs: 1.5, sm: 2 },
        width: '100%',
        maxWidth: 'none',
        mx: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ mb: { xs: 2, sm: 2.5 }, display: { xs: 'none', md: 'block' } }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {icon}
          <Box>
            <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
      {children}
    </Box>
  )
}
